import { Progress } from "@backstage/core-components";
import { alertApiRef, useApi } from "@backstage/core-plugin-api";
import { Spacer } from "@internal/plugin-utilities";
import {
  Button,
  Input,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Theme,
} from "@material-ui/core";
import React, { ChangeEvent, useState } from "react";
import { opsgenieApiRef } from "../../api";
import { useFilteredIncidentsWithPostMortems } from "../../hooks/useFilteredIncidentsWithPostMortems";
import { OpsgenieIncident } from "../../types";

const useStyles = makeStyles((_theme: Theme) => ({
  label: {
    marginBottom: "10px",
  },
  submit: {
    float: "right",
  },
}));

interface Props {}

export const ExistingPostMortemForm: React.FC<Props> = (): JSX.Element => {
  const classes = useStyles();

  const api = useApi(opsgenieApiRef);
  const alertApi = useApi(alertApiRef);

  const [selectedIncident, setSelectedIncident] = useState<string>("");
  const [postMortem, setPostMortem] = useState<string>("");
  const incidents = useFilteredIncidentsWithPostMortems();

  const onIncidentChange = (event: ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    setSelectedIncident(value);
  };

  const onPostMortemChange = (event: ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    setPostMortem(value);
  };

  const onSubmit = (event: any) => {
    event.preventDefault();
    if (selectedIncident === "" || !postMortem) {
      alertApi.post({
        message: "You must supply an Incident ID and Post Mortem ID",
        severity: "error",
      });
      return;
    }

    (async () => {
      const request = await api.getPostMortem({ postMortemId: postMortem });

      if (request.status === "error") {
        alertApi.post({
          message: request.error
            ? request.error
            : "Could not determine the problem. Reach out to the Radiator administrators.",
          severity: "error",
        });
        return;
      }
      const data = request.data.data;

      if (data.rootIncident.id !== selectedIncident) {
        alertApi.post({
          message:
            "The rootIncident attached to the Post Mortem does not match the Incident ID supplied.",
          severity: "error",
        });
        return;
      }

      const register = await api.registerPostMortem({
        incidentId: selectedIncident,
        postMortemId: postMortem,
      });

      if (register.status === "error") {
        alertApi.post({
          message: register.error
            ? register.error
            : "Could not determine the problem. Reach out to the Radiator administrators.",
          severity: "error",
        });
        return;
      }

      alertApi.post({
        message: register.data.message,
        severity: "success",
      });
    })();
  };

  if (incidents.loading) {
    return <Progress />;
  }

  return (
    <form id="post-mortem-form">
      <InputLabel className={classes.label}>
        Select the Incident for the Post Mortem:
      </InputLabel>
      <Select
        value={selectedIncident}
        onChange={onIncidentChange}
        required
        fullWidth
      >
        {incidents.results &&
          incidents.results
            .map((incident: OpsgenieIncident | undefined) => {
              if (incident) {
                return (
                  <MenuItem key={incident.id} value={incident.id}>
                    {incident.message}: [{incident.id}]
                  </MenuItem>
                );
              }
              return undefined;
            })
            .filter((f) => f)}
      </Select>
      <Spacer variant="double" />
      <InputLabel className={classes.label}>
        Enter the ID of the Post Mortem attached to the Incident
      </InputLabel>
      <Input onChange={onPostMortemChange} fullWidth />
      <Spacer variant="double" />
      <Button
        type="submit"
        onClick={onSubmit}
        variant="outlined"
        className={classes.submit}
      >
        Submit Post Mortem
      </Button>
    </form>
  );
};
